import { useState, useCallback, useRef } from "react";
import { ArrowLeftRight, Check } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CleanedRecord {
  tax_id: string;
  date_recorded: string;
  instrument_type: string;
  county: string;
  prefix: string;
  original_name: string;
  first_name: string;
  last_name: string;
  seller_address: string;
  seller_city: string;
  seller_state: string;
  seller_zip: string;
  amount: string;
  original_data?: string;
  issues_found?: string[];
}

interface DataCleanupTableProps {
  data: CleanedRecord[];
  onDataChange: (data: CleanedRecord[]) => void;
}

export function DataCleanupTable({ data, onDataChange }: DataCleanupTableProps) {
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savedCell, setSavedCell] = useState<{ row: number; field: string } | null>(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  const commitEdit = useCallback(() => {
    if (!editingCell) return;
    const { row, field } = editingCell;
    const newData = [...dataRef.current];
    if (newData[row][field as keyof CleanedRecord] !== editValue) {
      newData[row] = { ...newData[row], [field]: editValue };
      onDataChange(newData);
      setSavedCell({ row, field });
      setTimeout(() => setSavedCell(null), 1200);
    }
    setEditingCell(null);
  }, [editingCell, editValue, onDataChange]);

  const startEdit = (rowIndex: number, field: string, currentValue: string) => {
    if (editingCell) commitEdit();
    setEditingCell({ row: rowIndex, field });
    setEditValue(currentValue || "");
  };

  const handleSwapNames = (rowIndex: number) => {
    const newData = [...data];
    const record = newData[rowIndex];
    newData[rowIndex] = { ...record, first_name: record.last_name, last_name: record.first_name };
    onDataChange(newData);
  };

  const renderEditableCell = (record: CleanedRecord, rowIndex: number, field: keyof CleanedRecord) => {
    const isEditing = editingCell?.row === rowIndex && editingCell?.field === field;
    const justSaved = savedCell?.row === rowIndex && savedCell?.field === field;
    const value = record[field] as string;

    if (isEditing) {
      return (
        <Input
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setEditingCell(null);
            if (e.key === 'Tab') {
              e.preventDefault();
              commitEdit();
            }
          }}
          className="h-8"
        />
      );
    }

    return (
      <div
        onClick={() => startEdit(rowIndex, field, value)}
        className={`cursor-pointer hover:bg-accent/50 px-2 py-1 rounded min-h-[32px] flex items-center gap-1 transition-colors ${justSaved ? 'bg-green-500/15' : ''}`}
      >
        {value || <span className="text-muted-foreground italic">empty</span>}
        {justSaved && <Check className="h-3 w-3 text-green-600 shrink-0" />}
      </div>
    );
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Tax ID</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>County</TableHead>
            <TableHead className="w-[100px]">Prefix</TableHead>
            <TableHead className="text-muted-foreground">Original Name</TableHead>
            <TableHead>First Name</TableHead>
            <TableHead>Last Name</TableHead>
            <TableHead className="w-[50px]">Swap</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>City</TableHead>
            <TableHead className="w-[60px]">State</TableHead>
            <TableHead className="w-[80px]">Zip</TableHead>
            <TableHead className="w-[120px]">Amount</TableHead>
            <TableHead className="w-[80px]">Issues</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((record, index) => (
            <TableRow key={index}>
              <TableCell className="font-mono text-xs">
                {renderEditableCell(record, index, 'tax_id')}
              </TableCell>
              <TableCell>{renderEditableCell(record, index, 'date_recorded')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'instrument_type')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'county')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'prefix')}</TableCell>
              <TableCell className="text-xs text-muted-foreground italic">{record.original_name}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'first_name')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'last_name')}</TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => handleSwapNames(index)}
                  title="Swap first ↔ last name"
                >
                  <ArrowLeftRight className="h-3 w-3" />
                </Button>
              </TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_address')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_city')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_state')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'seller_zip')}</TableCell>
              <TableCell>{renderEditableCell(record, index, 'amount')}</TableCell>
              <TableCell>
                {record.issues_found && record.issues_found.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="cursor-help">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {record.issues_found.length}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[300px]">
                        <ul className="list-disc pl-4 space-y-1">
                          {record.issues_found.map((issue, i) => (
                            <li key={i} className="text-sm">{issue}</li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
